import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Eye,
  Megaphone,
  Pause,
  Play,
  Save,
  Send,
  Snowflake,
  XCircle,
} from 'lucide-react'
import { api } from '@/lib/api'

type CampaignKind = 'EMAIL' | 'SMS' | 'WHATSAPP_TEMPLATE' | 'WHATSAPP_BROADCAST'
type CampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'PAUSED'
  | 'FAILED'
  | 'CANCELLED'

interface Campaign {
  id: string
  kind: CampaignKind
  status: CampaignStatus
  name: string
  smartListId: string
  messageBody: string
  subject: string | null
  whatsappTemplateName: string | null
  language: string | null
  throttlePerHour: number
  totalRecipients: number
  sent: number
  delivered: number
  failed: number
  skipped: number
  scheduledAt: string | null
  frozenAt: string | null
  dispatchedAt: string | null
  finishedAt: string | null
  smartList: { id: string; name: string } | null
}

interface SmartListLite {
  id: string
  name: string
  // Devuelto por /v1/marketing/smart-lists (campo cached del modelo).
  contactCountCached?: number
}

interface PreviewResp {
  frozen: boolean
  count: number
  totalMemberships: number
  breakdown: Array<{ reason: string | null; count: number }>
  sample: Array<{
    id: string
    displayName: string
    email: string | null
    phoneE164: string | null
  }>
  hint?: string
}

interface Stats {
  status: CampaignStatus
  totalRecipients: number
  pending: number
  inFlight?: number
  sent: number
  delivered: number
  failed: number
  skipped: number
  opens: number
  clicks: number
  costMicrosTotal: string
  skippedBreakdown: Array<{ reason: string | null; count: number }>
  frozenAt: string | null
  dispatchedAt: string | null
  finishedAt: string | null
}

interface SendItem {
  id: string
  channelKind: string
  toAddress: string
  status: string
  sentAt: string
  deliveredAt: string | null
  error: string | null
  providerMessageId: string | null
  clientStub: {
    id: string
    displayName: string
    email: string | null
    phoneE164: string | null
  }
}

const KIND_LABEL: Record<CampaignKind, string> = {
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP_TEMPLATE: 'WhatsApp Plantilla',
  WHATSAPP_BROADCAST: 'WhatsApp Broadcast',
}

const STATUS_LABEL: Record<CampaignStatus, string> = {
  DRAFT: 'Borrador',
  SCHEDULED: 'Programada',
  SENDING: 'Enviando',
  SENT: 'Enviada',
  PAUSED: 'Pausada',
  FAILED: 'Fallida',
  CANCELLED: 'Cancelada',
}

const SKIP_REASON_STYLE: Record<string, string> = {
  NO_CONSENT: 'bg-red-50 border-red-200 text-red-800',
  DND: 'bg-amber-50 border-amber-200 text-amber-900',
  SUPPRESSED: 'bg-rose-50 border-rose-200 text-rose-800',
  NO_ADDRESS: 'bg-ink-900/5 border-ink-900/10 text-ink-700/80',
  OPT_OUT: 'bg-red-50 border-red-200 text-red-800',
  ERASED: 'bg-purple-50 border-purple-200 text-purple-800',
  OTHER: 'bg-ink-900/5 border-ink-900/10 text-ink-700/80',
}

const SKIP_REASON_LABEL: Record<string, string> = {
  NO_CONSENT: 'Sin consent',
  DND: 'En DND',
  SUPPRESSED: 'Suprimido',
  NO_ADDRESS: 'Sin dirección',
  OPT_OUT: 'Opt-out',
  ERASED: 'Cliente borrado (RGPD)',
  OTHER: 'Otro',
}

const SEND_STATUS_LABEL: Record<string, string> = {
  QUEUED: 'En cola',
  SENT: 'Enviado',
  DELIVERED: 'Entregado',
  FAILED: 'Fallido',
  SKIPPED: 'Saltado',
}

function statusPillClass(s: CampaignStatus): string {
  switch (s) {
    case 'DRAFT':
      return 'bg-ink-900/10 text-ink-700/70'
    case 'SCHEDULED':
      return 'bg-blueprint-50 text-blueprint-700'
    case 'SENDING':
      return 'bg-amber-50 text-amber-700'
    case 'SENT':
      return 'bg-emerald-50 text-emerald-700'
    case 'PAUSED':
      return 'bg-amber-100 text-amber-900'
    case 'FAILED':
      return 'bg-red-50 text-red-700'
    case 'CANCELLED':
      return 'bg-ink-900/10 text-ink-700/60'
  }
}

function isEditable(status: CampaignStatus): boolean {
  return status === 'DRAFT'
}

// Caps razonables por SMS: 160 chars GSM-7. Sólo aviso UI, el backend no
// rechaza (algunos providers soportan multi-part).
const SMS_CHAR_WARN = 160

export function CampaignEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'new' || !id

  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [smartLists, setSmartLists] = useState<SmartListLite[]>([])

  // Form fields (modo edición y modo nueva).
  const [kind, setKind] = useState<CampaignKind>('EMAIL')
  const [name, setName] = useState('')
  const [smartListId, setSmartListId] = useState('')
  const [messageBody, setMessageBody] = useState('')
  const [subject, setSubject] = useState('')
  const [waTemplate, setWaTemplate] = useState('')
  const [language, setLanguage] = useState('es')
  // Permitimos string vacío para que el usuario pueda borrar y reescribir
  // sin que el campo "salte" a 1 entre teclas. La validación al guardar
  // garantiza >=1.
  const [throttle, setThrottle] = useState<number | ''>(1000)

  const [preview, setPreview] = useState<PreviewResp | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [sends, setSends] = useState<SendItem[]>([])

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  // Modal de confirmación Blueprint en lugar de window.confirm: nos permite
  // mostrar breakdown, canal y aviso de auto-freeze antes de un envío
  // irreversible — control humano efectivo (AI Act art. 14 + buenas
  // prácticas LSSI).
  const [confirmModal, setConfirmModal] = useState<{
    kind: 'dispatch' | 'freeze' | 'cancel'
    title: string
    message: string
    details?: React.ReactNode
    confirmLabel: string
    tone: 'primary' | 'danger'
    onConfirm: () => Promise<void>
  } | null>(null)

  const editable = !campaign || isEditable(campaign.status)
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  async function loadSmartLists() {
    const r = await api<{ items: SmartListLite[] }>('/v1/marketing/smart-lists')
    setSmartLists(r.items)
  }

  async function loadCampaign(cid: string) {
    const c = await api<Campaign>(`/v1/marketing/campaigns/${cid}`)
    setCampaign(c)
    setKind(c.kind)
    setName(c.name)
    setSmartListId(c.smartListId)
    setMessageBody(c.messageBody)
    setSubject(c.subject ?? '')
    setWaTemplate(c.whatsappTemplateName ?? '')
    setLanguage(c.language ?? 'es')
    setThrottle(c.throttlePerHour ?? 1)
  }

  async function loadStats(cid: string) {
    const s = await api<Stats>(`/v1/marketing/campaigns/${cid}/stats`)
    setStats(s)
  }

  async function loadSends(cid: string) {
    const r = await api<{ items: SendItem[] }>(
      `/v1/marketing/campaigns/${cid}/sends?limit=30`,
    )
    setSends(r.items)
  }

  useEffect(() => {
    void loadSmartLists()
  }, [])

  useEffect(() => {
    if (isNew) return
    void loadCampaign(id!)
    void loadStats(id!)
    void loadSends(id!)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Polling de stats + sends mientras la campaña está SENDING. Cada 5s.
  // Se detiene en cuanto el status pasa a SENT/PAUSED/FAILED/CANCELLED o
  // el componente se desmonta.
  useEffect(() => {
    if (isNew || !id) return
    if (!campaign || campaign.status !== 'SENDING') return
    let cancelled = false
    let ticks = 0
    const interval = setInterval(async () => {
      if (cancelled) return
      ticks++
      await Promise.all([loadStats(id), loadCampaign(id)])
      // /sends se refresca menos para no martillear el backend.
      if (ticks % 3 === 0) await loadSends(id)
    }, 5000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, campaign?.status])

  // Limpieza del timer de flash si el componente se desmonta antes.
  useEffect(() => {
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current)
    }
  }, [])

  function flash(msg: string) {
    setOk(msg)
    if (flashTimer.current) clearTimeout(flashTimer.current)
    flashTimer.current = setTimeout(() => {
      setOk(null)
      flashTimer.current = null
    }, 3000)
  }

  async function save() {
    setBusy(true)
    setErr(null)
    try {
      const body = {
        kind,
        smartListId,
        name,
        messageBody,
        subject: kind === 'EMAIL' ? subject : undefined,
        whatsappTemplateName:
          kind === 'WHATSAPP_TEMPLATE' ? waTemplate : undefined,
        language: kind.startsWith('WHATSAPP') ? language : undefined,
        throttlePerHour: throttle === '' ? 1 : Math.max(1, Number(throttle)),
      }
      if (isNew) {
        const created = await api<Campaign>('/v1/marketing/campaigns', {
          method: 'POST',
          body: JSON.stringify(body),
        })
        flash('Borrador creado')
        navigate(`/marketing/campaigns/${created.id}`, { replace: true })
      } else {
        await api(`/v1/marketing/campaigns/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        })
        flash('Cambios guardados')
        await loadCampaign(id!)
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function runPreview() {
    if (isNew || !id) {
      setErr('Guarda el borrador antes de previsualizar')
      return
    }
    setBusy(true)
    setErr(null)
    try {
      const p = await api<PreviewResp>(`/v1/marketing/campaigns/${id}/preview`)
      setPreview(p)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function freeze() {
    if (!id) return
    setConfirmModal({
      kind: 'freeze',
      title: 'Congelar recipients',
      message:
        'Fija la lista de destinatarios actual. Tras congelar, los cambios en la SmartList ya no afectan a esta campaña.',
      details: (
        <div className="text-xs font-mono text-ink-700/70">
          Puedes recongelar mientras la campaña esté en borrador.
        </div>
      ),
      confirmLabel: 'Congelar',
      tone: 'primary',
      onConfirm: async () => {
        setBusy(true)
        setErr(null)
        try {
          await api(`/v1/marketing/campaigns/${id}/freeze`, { method: 'POST' })
          flash('Recipients congelados')
          await loadCampaign(id)
          await runPreview()
          await loadStats(id)
        } catch (e) {
          setErr(e instanceof Error ? e.message : String(e))
        } finally {
          setBusy(false)
        }
      },
    })
  }

  function dispatch() {
    if (!id) return
    const recipients = preview?.count ?? campaign?.totalRecipients ?? 0
    const breakdown = preview?.breakdown ?? []
    const skippedTotal = breakdown.reduce((s, b) => s + b.count, 0)
    const channel = campaign ? KIND_LABEL[campaign.kind] : '—'
    const needsFreeze = !campaign?.frozenAt
    setConfirmModal({
      kind: 'dispatch',
      title: 'Enviar ahora',
      message: `Vas a enviar a ${recipients} contacto${recipients === 1 ? '' : 's'} por ${channel}. Acción irreversible.`,
      details: (
        <div className="space-y-2 text-xs font-mono text-ink-700/80">
          {needsFreeze && (
            <div className="flex items-start gap-2 px-2 py-1 rounded border bg-amber-50 border-amber-200 text-amber-900">
              <AlertTriangle className="size-3 shrink-0 mt-0.5" />
              <span>
                Los recipients no están congelados. Pulsa primero "Congelar recipients" y revisa el desglose antes de enviar.
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">
              <div className="text-[10px] uppercase tracking-wider">se enviarán</div>
              <div className="text-base">{recipients}</div>
            </div>
            <div className="rounded border border-ink-900/15 bg-ink-900/5 px-2 py-1">
              <div className="text-[10px] uppercase tracking-wider text-ink-700/60">
                se omitirán
              </div>
              <div className="text-base">{skippedTotal}</div>
            </div>
          </div>
          {breakdown.length > 0 && (
            <div className="space-y-0.5">
              {breakdown.map((b, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-2 py-0.5 rounded border bg-white border-ink-900/10"
                >
                  <span>{SKIP_REASON_LABEL[b.reason ?? 'OTHER'] ?? b.reason ?? 'OTHER'}</span>
                  <span>{b.count}</span>
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-ink-700/60">
            Cada envío pasa por guards de consent · DND · supresión antes de salir.
          </div>
        </div>
      ),
      confirmLabel: 'Enviar ahora',
      tone: 'danger',
      onConfirm: async () => {
        setBusy(true)
        setErr(null)
        try {
          await api(`/v1/marketing/campaigns/${id}/dispatch`, { method: 'POST' })
          flash('Campaña encolada — envíos en curso')
          await loadCampaign(id)
          await loadStats(id)
          await loadSends(id)
        } catch (e) {
          setErr(e instanceof Error ? e.message : String(e))
        } finally {
          setBusy(false)
        }
      },
    })
  }

  async function pause() {
    if (!id) return
    setBusy(true)
    try {
      await api(`/v1/marketing/campaigns/${id}/pause`, { method: 'POST' })
      flash('Campaña pausada')
      await loadCampaign(id)
      await loadStats(id)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function resume() {
    if (!id) return
    setBusy(true)
    try {
      await api(`/v1/marketing/campaigns/${id}/resume`, { method: 'POST' })
      flash('Campaña reanudada')
      await loadCampaign(id)
      await loadStats(id)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function cancel() {
    if (!id) return
    setConfirmModal({
      kind: 'cancel',
      title: 'Cancelar campaña',
      message: 'La campaña pasará a estado CANCELLED y no se podrá reanudar.',
      confirmLabel: 'Cancelar campaña',
      tone: 'danger',
      onConfirm: async () => {
        setBusy(true)
        try {
          await api(`/v1/marketing/campaigns/${id}/cancel`, { method: 'POST' })
          flash('Campaña cancelada')
          await loadCampaign(id)
          await loadStats(id)
        } catch (e) {
          setErr(e instanceof Error ? e.message : String(e))
        } finally {
          setBusy(false)
        }
      },
    })
  }

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4 flex items-start gap-3">
        <Link to="/marketing/campaigns" className="bp-btn">
          <ArrowLeft className="size-3" />
        </Link>
        <div className="flex-1">
          <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <Megaphone className="size-5 text-blueprint-700" />
            {isNew ? 'Nueva campaña' : (campaign?.name ?? 'Cargando…')}
          </h1>
          <p className="text-xs font-mono text-ink-700/60">
            {campaign && (
              <span>
                <span className="bp-pill">{KIND_LABEL[campaign.kind]}</span>{' '}
                <span
                  className={['bp-pill', statusPillClass(campaign.status)].join(' ')}
                >
                  {campaign.status === 'SENDING' && (
                    <span className="inline-block size-1.5 rounded-full bg-amber-500 animate-pulse mr-1 align-middle" />
                  )}
                  {STATUS_LABEL[campaign.status]}
                </span>
              </span>
            )}
          </p>
        </div>
      </header>

      {err && (
        <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {err}
        </div>
      )}
      {ok && (
        <div className="mb-3 text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
          {ok}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Editor */}
        <div className="col-span-7 bp-card p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                canal
              </span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as CampaignKind)}
                disabled={!editable}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white disabled:bg-ink-900/5"
              >
                {(Object.keys(KIND_LABEL) as CampaignKind[]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                SmartList
              </span>
              <select
                value={smartListId}
                onChange={(e) => setSmartListId(e.target.value)}
                disabled={!editable}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white disabled:bg-ink-900/5"
              >
                <option value="">— elegir —</option>
                {smartLists.map((sl) => (
                  <option key={sl.id} value={sl.id}>
                    {sl.name}
                    {typeof sl.contactCountCached === 'number'
                      ? ` · ${sl.contactCountCached} contactos`
                      : ''}
                  </option>
                ))}
              </select>
              {smartListId &&
                (() => {
                  const sel = smartLists.find((s) => s.id === smartListId)
                  if (!sel || typeof sel.contactCountCached !== 'number') return null
                  return (
                    <div className="mt-1 text-[10px] font-mono text-ink-700/60">
                      cached: {sel.contactCountCached} contactos · el número real
                      tras guards se calcula en el freeze.
                    </div>
                  )
                })()}
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              nombre interno
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!editable}
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm disabled:bg-ink-900/5"
            />
          </label>

          {kind === 'EMAIL' && (
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                asunto
              </span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={!editable}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm disabled:bg-ink-900/5"
              />
            </label>
          )}

          {kind === 'WHATSAPP_TEMPLATE' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                  nombre plantilla Meta
                </span>
                <input
                  value={waTemplate}
                  onChange={(e) => setWaTemplate(e.target.value)}
                  disabled={!editable}
                  className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono disabled:bg-ink-900/5"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                  idioma
                </span>
                <input
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={!editable}
                  className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono disabled:bg-ink-900/5"
                />
              </label>
            </div>
          )}

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              cuerpo · variables {`{{nombre}}`} {`{{email}}`} {`{{phone}}`}
            </span>
            <textarea
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              disabled={!editable}
              rows={10}
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono disabled:bg-ink-900/5"
              placeholder="Hola {{nombre}}, …"
            />
            <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-ink-700/60">
              <span>{messageBody.length} caracteres</span>
              {kind === 'SMS' && messageBody.length > SMS_CHAR_WARN && (
                <span className="text-amber-700">
                  &gt; {SMS_CHAR_WARN} chars: el envío se fragmenta en varios SMS
                </span>
              )}
              {kind === 'EMAIL' && (
                <span className="text-ink-700/50">
                  se inyectará footer de baja LSSI
                </span>
              )}
            </div>
          </label>

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              throttle (envíos / hora) — protege reputation
            </span>
            <input
              type="number"
              min={1}
              value={throttle}
              onChange={(e) => {
                const v = e.target.value
                if (v === '') setThrottle('')
                else setThrottle(Math.max(1, Number(v) || 1))
              }}
              onBlur={() => {
                if (throttle === '') setThrottle(1)
              }}
              disabled={!editable}
              className="mt-1 w-32 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono disabled:bg-ink-900/5"
            />
          </label>

          {/* Stepper lineal: Borrador → Vista previa → Congelar → Enviar.
              Cada paso muestra check verde si está completo. */}
          {!isNew && (
            <div className="pt-3 border-t border-ink-900/10">
              <ol className="flex items-center gap-1 text-[10px] font-mono">
                <StepperItem n={1} label="Borrador" done={!!campaign} />
                <StepperSep />
                <StepperItem n={2} label="Vista previa" done={preview != null} />
                <StepperSep />
                <StepperItem
                  n={3}
                  label="Congelar"
                  done={!!campaign?.frozenAt}
                />
                <StepperSep />
                <StepperItem
                  n={4}
                  label="Enviar"
                  done={!!campaign?.dispatchedAt}
                />
              </ol>
            </div>
          )}

          <div className="flex gap-2 pt-2 flex-wrap">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !editable || !name.trim() || !smartListId || !messageBody.trim()}
              className="bp-btn-primary"
              title="Guarda el borrador. Sólo editable mientras la campaña esté en DRAFT."
            >
              <Save className="size-3" /> Guardar borrador
            </button>
            {!isNew && (
              <button
                type="button"
                onClick={() => void runPreview()}
                disabled={busy}
                className="bp-btn"
                title="Calcula cuántos contactos cumplen el segmento y cuántos quedarán fuera por consent/DND."
              >
                <Eye className="size-3" /> Vista previa
              </button>
            )}
            {!isNew && campaign && (campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
              <>
                <button
                  type="button"
                  onClick={freeze}
                  disabled={busy}
                  className="bp-btn"
                  title="Fija la lista de destinatarios. Tras congelar, los cambios en la SmartList ya no afectan a esta campaña."
                >
                  <Snowflake className="size-3" /> Congelar recipients
                </button>
                <button
                  type="button"
                  onClick={dispatch}
                  disabled={busy}
                  className="bp-btn-primary ml-auto bg-red-700 hover:bg-red-800 border-red-700"
                  title="Encola los envíos respetando guards de consent en cada destinatario. Acción IRREVERSIBLE."
                >
                  <Send className="size-3" /> Enviar ahora
                </button>
              </>
            )}
            {!isNew && campaign && campaign.status === 'SENDING' && (
              <button
                type="button"
                onClick={() => void pause()}
                disabled={busy}
                className="bp-btn ml-auto"
                title="Detiene los siguientes envíos. El batch en curso se aborta antes del próximo mensaje."
              >
                <Pause className="size-3" /> Pausar
              </button>
            )}
            {!isNew && campaign && (campaign.status === 'PAUSED' || campaign.status === 'FAILED') && (
              <button
                type="button"
                onClick={() => void resume()}
                disabled={busy}
                className="bp-btn-primary ml-auto"
                title="Reanuda los envíos pendientes desde el siguiente tick."
              >
                <Play className="size-3" /> Reanudar
              </button>
            )}
            {!isNew &&
              campaign &&
              !['SENT', 'CANCELLED'].includes(campaign.status) && (
                <button
                  type="button"
                  onClick={cancel}
                  disabled={busy}
                  className="bp-btn text-red-700 border-red-200"
                  title="Cancela la campaña — no se podrá reanudar."
                >
                  <XCircle className="size-3" /> Cancelar
                </button>
              )}
          </div>
        </div>

        {/* Sidebar: preview + stats */}
        <div className="col-span-5 space-y-4">
          {!preview && !stats && sends.length === 0 && (
            <div className="bp-card p-4 text-xs font-mono text-ink-700/60 flex items-start gap-3">
              <Eye className="size-4 text-ink-700/30 shrink-0 mt-0.5" />
              <div>
                <div className="text-ink-700/80 mb-1">Vista previa y stats</div>
                Aparecerán aquí cuando guardes el borrador y ejecutes "Vista previa".
                Al congelar verás el desglose por cumplimiento (sin consent · DND ·
                supresión) y al enviar se actualizarán en directo.
              </div>
            </div>
          )}
          {preview && (
            <div className="bp-card p-3">
              <div className="text-sm font-medium">
                {preview.count} contacto{preview.count === 1 ? '' : 's'} {preview.frozen ? 'congelados' : 'cumplirían'}
              </div>
              {preview.hint && (
                <div className="mt-1 text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  {preview.hint}
                </div>
              )}
              {preview.breakdown.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                    excluidos por compliance
                  </div>
                  {preview.breakdown.map((b, i) => (
                    <div
                      key={i}
                      className={[
                        'text-xs px-2 py-1 rounded border flex items-center justify-between',
                        SKIP_REASON_STYLE[b.reason ?? 'OTHER'] ?? SKIP_REASON_STYLE.OTHER,
                      ].join(' ')}
                    >
                      <span>{SKIP_REASON_LABEL[b.reason ?? 'OTHER'] ?? b.reason ?? 'OTHER'}</span>
                      <span className="font-mono">{b.count}</span>
                    </div>
                  ))}
                </div>
              )}
              {preview.sample.length > 0 && (
                <div className="mt-3 border-t border-ink-900/10 pt-2">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-1">
                    muestra
                  </div>
                  <ul className="space-y-0.5">
                    {preview.sample.map((s) => (
                      <li key={s.id}>
                        <Link
                          to={`/marketing/clients/${s.id}`}
                          title={`Ver ficha de ${s.displayName}`}
                          className="block px-2 py-1 text-xs hover:bg-surface-alt rounded"
                        >
                          <span className="text-blueprint-700">{s.displayName}</span>{' '}
                          <span className="font-mono text-ink-700/60">
                            {s.email ?? s.phoneE164 ?? ''}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stats && campaign && ['SENDING', 'SENT', 'PAUSED', 'FAILED'].includes(campaign.status) && (
            <div className="bp-card p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-2">
                stats
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Pendientes" value={stats.pending} tone="ink" />
                <Stat label="Enviados" value={stats.sent} tone="emerald" />
                <Stat label="Entregados" value={stats.delivered} tone="emerald" />
                <Stat label="Fallidos" value={stats.failed} tone="red" />
                <Stat label="Saltados" value={stats.skipped} tone="amber" />
                <Stat label="Total" value={stats.totalRecipients} tone="ink" />
              </div>
              {stats.skippedBreakdown.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                    desglose saltados
                  </div>
                  {stats.skippedBreakdown.map((b, i) => (
                    <div
                      key={i}
                      className={[
                        'text-xs px-2 py-1 rounded border flex items-center justify-between',
                        SKIP_REASON_STYLE[b.reason ?? 'OTHER'] ?? SKIP_REASON_STYLE.OTHER,
                      ].join(' ')}
                    >
                      <span>{SKIP_REASON_LABEL[b.reason ?? 'OTHER'] ?? b.reason ?? 'OTHER'}</span>
                      <span className="font-mono">{b.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {sends.length > 0 && (
            <div className="bp-card overflow-hidden">
              <div className="px-3 py-2 border-b border-ink-900/10 text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                envíos recientes ({sends.length})
              </div>
              <table className="w-full text-xs">
                <thead className="bg-surface-alt text-[10px] font-mono uppercase text-ink-700/60">
                  <tr>
                    <th className="px-2 py-1 text-left">Destinatario</th>
                    <th className="px-2 py-1 text-left">Estado</th>
                    <th className="px-2 py-1 text-right">Cuándo</th>
                  </tr>
                </thead>
                <tbody>
                  {sends.map((s) => (
                    <tr key={s.id} className="border-t border-ink-900/5">
                      <td className="px-2 py-1">
                        <Link
                          to={`/marketing/clients/${s.clientStub.id}`}
                          className="text-blueprint-700"
                        >
                          {s.clientStub.displayName}
                        </Link>
                        <div className="font-mono text-[10px] text-ink-700/50">{s.toAddress}</div>
                      </td>
                      <td className="px-2 py-1">
                        <span
                          className={[
                            'bp-pill',
                            s.status === 'DELIVERED'
                              ? 'bg-emerald-50 text-emerald-700'
                              : s.status === 'SENT'
                                ? 'bg-blueprint-50 text-blueprint-700'
                                : s.status === 'FAILED'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-ink-900/10 text-ink-700/60',
                          ].join(' ')}
                        >
                          {SEND_STATUS_LABEL[s.status] ?? s.status}
                        </span>
                        {s.error && (
                          <div className="mt-1 text-[10px] text-red-700">{s.error}</div>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-[10px] text-ink-700/50">
                        {new Date(s.sentAt).toLocaleString('es-ES')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación Blueprint — reemplaza window.confirm para que
          el operador vea breakdown + canal antes de cualquier acción
          irreversible (control humano efectivo). */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 bg-ink-900/40 flex items-center justify-center p-4">
          <div data-testid="confirm-modal" className="bp-card w-full max-w-md p-4">
            <div className="text-base font-medium mb-2 flex items-center gap-2">
              {confirmModal.tone === 'danger' && (
                <AlertTriangle className="size-4 text-red-700" />
              )}
              {confirmModal.title}
            </div>
            <div className="text-sm text-ink-700/80 mb-3">{confirmModal.message}</div>
            {confirmModal.details && <div className="mb-3">{confirmModal.details}</div>}
            <div className="flex gap-2 justify-end pt-2 border-t border-ink-900/10">
              <button
                type="button"
                className="bp-btn"
                onClick={() => setConfirmModal(null)}
              >
                Cancelar
              </button>
              <button
                data-testid="confirm-modal-accept"
                type="button"
                className={
                  confirmModal.tone === 'danger'
                    ? 'bp-btn-primary bg-red-700 hover:bg-red-800 border-red-700'
                    : 'bp-btn-primary'
                }
                onClick={async () => {
                  const fn = confirmModal.onConfirm
                  setConfirmModal(null)
                  await fn()
                }}
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function StepperItem({ n, label, done }: { n: number; label: string; done: boolean }) {
  return (
    <li
      className={[
        'flex items-center gap-1 px-1.5 py-0.5 rounded',
        done ? 'text-emerald-700' : 'text-ink-700/60',
      ].join(' ')}
    >
      <span
        className={[
          'inline-flex size-4 items-center justify-center rounded-full text-[9px]',
          done
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-ink-900/10 text-ink-700/60',
        ].join(' ')}
      >
        {done ? <Check className="size-2.5" /> : n}
      </span>
      <span className="uppercase tracking-wider">{label}</span>
    </li>
  )
}

function StepperSep() {
  return <li aria-hidden className="text-ink-700/30">·</li>
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'emerald' | 'red' | 'amber' | 'ink'
}) {
  const cls =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700'
      : tone === 'red'
        ? 'bg-red-50 text-red-700'
        : tone === 'amber'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-ink-900/5 text-ink-900'
  return (
    <div className={['rounded px-2 py-2', cls].join(' ')}>
      <div className="text-lg font-medium font-mono">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{label}</div>
    </div>
  )
}
